"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import type { ContractDocumentVariant } from "@/lib/services/esignature/contract-document-name";

/**
 * Drives the contract raw-document viewer.
 *
 * Flow: GET the document → if it's archived we get a presigned URL straight
 * away → if not, POST to ask the provider to render it and poll GET until the
 * `contract-pdf-generated` webhook lands the archive.
 *
 * The presigned URL is short-lived by design, so this hook refetches rather
 * than caching one across a long-open modal.
 */

export type ContractDocumentPhase =
  | "idle"
  | "loading"
  | "ready"
  | "generating"
  | "unavailable"
  | "error";

export interface ContractDocumentState {
  phase: ContractDocumentPhase;
  variant: ContractDocumentVariant | null;
  url: string | null;
  filename: string | null;
  displayName: string | null;
  driveWebViewLink: string | null;
  archivedAt: string | null;
  /** Set when unavailable/error — already user-facing, safe to render. */
  message: string | null;
  /** True when an on-demand render is possible from the current state. */
  canGenerate: boolean;
  /** Seconds spent in `generating`, so the UI can reassure on a slow render. */
  elapsedSeconds: number;
}

interface DocumentResponse {
  status?: "ready" | "unavailable";
  variant?: ContractDocumentVariant;
  url?: string;
  filename?: string;
  displayName?: string;
  driveWebViewLink?: string | null;
  archivedAt?: string | null;
  reason?: string;
  canGenerate?: boolean;
  error?: string;
}

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 24; // ~60s — matches the existing preview poller.

const INITIAL: ContractDocumentState = {
  phase: "idle",
  variant: null,
  url: null,
  filename: null,
  displayName: null,
  driveWebViewLink: null,
  archivedAt: null,
  message: null,
  canGenerate: false,
  elapsedSeconds: 0,
};

export function useContractDocument(contractId: string | null) {
  const [state, setState] = useState<ContractDocumentState>(INITIAL);
  const cancelledRef = useRef(false);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTicker = useCallback(() => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
  }, []);

  // Stop polling if the modal/tab unmounts mid-generation.
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      stopTicker();
    };
  }, [stopTicker]);

  const fetchDocument = useCallback(
    async (variant?: ContractDocumentVariant): Promise<DocumentResponse | null> => {
      if (!contractId) return null;
      const query = variant ? `?variant=${variant}` : "";
      const response = await fetch(`/api/contracts/${contractId}/document${query}`);
      const body = (await response.json()) as DocumentResponse;
      if (!response.ok) {
        throw new Error(body.error || "Couldn't load this document.");
      }
      return body;
    },
    [contractId],
  );

  const applyResponse = useCallback((body: DocumentResponse) => {
    if (body.status === "ready" && body.url) {
      setState({
        phase: "ready",
        variant: body.variant ?? null,
        url: body.url,
        filename: body.filename ?? null,
        displayName: body.displayName ?? null,
        driveWebViewLink: body.driveWebViewLink ?? null,
        archivedAt: body.archivedAt ?? null,
        message: null,
        canGenerate: false,
        elapsedSeconds: 0,
      });
      return true;
    }
    setState({
      ...INITIAL,
      phase: "unavailable",
      variant: body.variant ?? null,
      message: body.reason ?? "No copy of this document is available.",
      canGenerate: Boolean(body.canGenerate),
    });
    return false;
  }, []);

  /** Load (or reload) the document for a variant. */
  const load = useCallback(
    async (variant?: ContractDocumentVariant) => {
      if (!contractId) return;
      stopTicker();
      setState((prev) => ({ ...prev, phase: "loading", message: null }));
      try {
        const body = await fetchDocument(variant);
        if (cancelledRef.current || !body) return;
        applyResponse(body);
      } catch (error) {
        if (cancelledRef.current) return;
        setState({
          ...INITIAL,
          phase: "error",
          message: error instanceof Error ? error.message : "Couldn't load this document.",
        });
      }
    },
    [contractId, fetchDocument, applyResponse, stopTicker],
  );

  /**
   * Ask the provider to render the agreement, then poll until the archive
   * lands. Only meaningful for the `unsigned` variant — an executed PDF can't
   * be generated on request, it only exists once everyone has signed.
   */
  const generate = useCallback(async () => {
    if (!contractId) return;
    stopTicker();
    setState((prev) => ({ ...prev, phase: "generating", message: null, elapsedSeconds: 0 }));
    tickerRef.current = setInterval(
      () => setState((prev) => ({ ...prev, elapsedSeconds: prev.elapsedSeconds + 1 })),
      1000,
    );

    try {
      const response = await fetch(`/api/contracts/${contractId}/document`, { method: "POST" });
      if (!response.ok) {
        const body = (await response.json()) as DocumentResponse;
        throw new Error(body.error || "Couldn't request this document.");
      }
    } catch (error) {
      stopTicker();
      if (cancelledRef.current) return;
      setState({
        ...INITIAL,
        phase: "error",
        message: error instanceof Error ? error.message : "Couldn't request this document.",
        canGenerate: true,
      });
      return;
    }

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (cancelledRef.current) return;
      try {
        const body = await fetchDocument("unsigned");
        if (cancelledRef.current || !body) return;
        if (body.status === "ready" && body.url) {
          stopTicker();
          applyResponse(body);
          return;
        }
      } catch {
        // Transient — keep polling; the loop's own timeout is the backstop.
      }
    }

    stopTicker();
    if (cancelledRef.current) return;
    setState((prev) => ({
      ...INITIAL,
      phase: "unavailable",
      variant: prev.variant,
      canGenerate: true,
      message: "The document is taking longer than expected to render. Try again.",
    }));
  }, [contractId, fetchDocument, applyResponse, stopTicker]);

  return { ...state, load, generate };
}
